import React from 'react';
import { FieldArray, useField } from 'formik';
import { TextField, JsonField } from './elements';

// Formik sub-forms for the special filter kinds (see filterKinds). Each receives
// `base` — the Formik path prefix for that filter — and owns its row layout.

// Axis metric switcher (kind: axis-switcher): lets the user pick which data
// field drives the X axis. Options are explicit {label, field} pairs so any
// dataset with multiple column variants can use this — not just p<N>_* names.
// When an X axis band is configured in Advanced, a band option is added
// to the sidebar picker automatically at runtime.
//
// `showWhenField` gates the whole control on a field being present in the
// current rows. It states the rule directly ("this switcher belongs to
// trace-replay rows"); the runtime also drops options whose field has no data,
// but that only agrees by coincidence — a Fixed row may carry a second
// percentile, and then the ungated switcher would appear where it does not belong.
export function AxisSwitcherEditor({ base }) {
  const [field] = useField(`${base}.options`);
  const options = field.value || [];

  return (
    <FieldArray name={`${base}.options`}>
      {({ push, remove }) => (
        <div className='row'>
          <div className='col-12'>
            {options.map((opt, i) => (
              <div key={i} className='row align-items-end mb-1'>
                <div className='col-5'>
                  <TextField name={`${base}.options.${i}.label`} label={i === 0 ? 'Label' : ''} placeholder='P90'/>
                </div>
                <div className='col-6'>
                  <TextField name={`${base}.options.${i}.field`} label={i === 0 ? 'Field' : ''} placeholder='p90_value'/>
                </div>
                <div className='col-1 align-self-end mb-1'>
                  <button type='button' className='btn btn-danger btn-sm' onClick={() => remove(i)}>×</button>
                </div>
              </div>
            ))}
            <button
              type='button'
              className='btn btn-outline-primary btn-sm mt-1'
              onClick={() => push({ label: '', field: '' })}
            >
              + Add option
            </button>
            <p className='nv-chart-settings-form__hint'>
              Each option appears in the sidebar and switches the X axis to the given
              field. When an X axis band is configured in Advanced, a band option
              is added automatically.
            </p>
          </div>
          <div className='col-6 mt-2'>
            <TextField name={`${base}.showWhenField`} label='Only show when rows have' placeholder='(field name)'
              hint='Optional. Hides the whole switcher unless the rows in the current selection carry this field — use it when the control only applies to one kind of row. Empty: always shown.'/>
          </div>
        </div>
      )}
    </FieldArray>
  );
}

// Grouped dropdown (kind: grouped-dropdown): a dropdown whose options are
// organised into named sections. Each section draws its options from a distinct
// data field; any number of sections can be configured. Selecting an option
// applies a filter on that section's field and clears all other sections'
// filters so the chart never mixes option groups.
export function GroupedDropdownEditor({ base }) {
  const [field] = useField(`${base}.sections`);
  const sections = field.value || [];

  return (
    <FieldArray name={`${base}.sections`}>
      {({ push, remove }) => (
        <div className='row'>
          <div className='col-12'>
            {sections.map((s, i) => (
              <div key={i} className='row align-items-end mb-1'>
                <div className='col-5'>
                  <TextField name={`${base}.sections.${i}.field`} label={i === 0 ? 'Field' : ''} placeholder='category_field'/>
                </div>
                <div className='col-6'>
                  <TextField name={`${base}.sections.${i}.label`} label={i === 0 ? 'Section label' : ''} placeholder='Group A'/>
                </div>
                <div className='col-1 align-self-end mb-1'>
                  <button type='button' className='btn btn-danger btn-sm' onClick={() => remove(i)}>×</button>
                </div>
              </div>
            ))}
            <button
              type='button'
              className='btn btn-outline-primary btn-sm mt-1'
              onClick={() => push({ field: '', label: '' })}
            >
              + Add section
            </button>
            <p className='nv-chart-settings-form__hint'>
              Each section becomes a named group in the dropdown. Options are
              the distinct values of that field in the data. Sections appear in
              the order listed.
            </p>
          </div>
          <div className='col-6 mt-2'>
            <TextField name={`${base}.scopeAttribute`} label='Scope field' placeholder='model'
              hint='Optional. Narrows options to rows matching the current value of this field.'/>
          </div>
          <div className='col-12 mt-2'>
            <JsonField
              name={`${base}.valueLabels`}
              label='Value labels (JSON)'
              rows={3}
              placeholder={'{ "agentic": "Agent Workload" }'}
              help='Maps a raw field value to a display label. Unlisted values are shown title-cased.'
            />
          </div>
        </div>
      )}
    </FieldArray>
  );
}
